import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import APIService from '../services/api';
import { AppContextType, Category } from '../types';

interface AppState {
  categories: Category[]; // Cambiado de string[] a Category[]
  favoriteProducts: number[];
  isLoading: boolean;
}

type AppAction =
  | { type: 'SET_CATEGORIES'; payload: Category[] } // Cambiado de string[] a Category[]
  | { type: 'SET_FAVORITES'; payload: number[] }
  | { type: 'TOGGLE_FAVORITE'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: AppState = {
  categories: [],
  favoriteProducts: [],
  isLoading: false,
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_FAVORITES':
      return { ...state, favoriteProducts: action.payload };
    case 'TOGGLE_FAVORITE':
      const productId = action.payload;
      const favorites = state.favoriteProducts.includes(productId)
        ? state.favoriteProducts.filter(id => id !== productId)
        : [...state.favoriteProducts, productId];
      return { ...state, favoriteProducts: favorites };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps): JSX.Element => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load stored favorites on app start
  useEffect(() => {
    loadStoredFavorites();
    loadCategories();
  }, []);

  // Save favorites to AsyncStorage whenever they change
  useEffect(() => {
    saveStoredFavorites();
  }, [state.favoriteProducts]);

  const loadStoredFavorites = async () => {
    try {
      const storedFavorites = await AsyncStorage.getItem('favorite_products');
      if (storedFavorites) {
        const favorites = JSON.parse(storedFavorites);
        dispatch({ type: 'SET_FAVORITES', payload: favorites });
      }
    } catch (error) {
      console.error('Error loading stored favorites:', error);
    }
  };

  const saveStoredFavorites = async () => {
    try {
      await AsyncStorage.setItem('favorite_products', JSON.stringify(state.favoriteProducts));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  };

  const loadCategories = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const categories = await APIService.getCategories();
      dispatch({ type: 'SET_CATEGORIES', payload: categories });
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const toggleFavorite = async (productId: number): Promise<void> => {
    try {
      // Optimistically update UI
      dispatch({ type: 'TOGGLE_FAVORITE', payload: productId });
      
      // Try to sync with server
      await APIService.toggleFavorite(productId);
    } catch (error) {
      // Revert on error
      dispatch({ type: 'TOGGLE_FAVORITE', payload: productId });
      console.error('Error toggling favorite:', error);
      throw error;
    }
  };

  const contextValue: AppContextType = {
    categories: state.categories,
    favoriteProducts: state.favoriteProducts,
    toggleFavorite,
    loadCategories,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
